/**
 * API Client for Yuklar Backend
 */
import type { ErrorCode } from "@shared/error-codes.js";
import { loadRuntimeConfig } from "@/lib/runtime-config";
import { getLanguage } from "@/lib/i18n/config";

type FieldError = { code: string; message: string };

/**
 * Codes carried by ApiError. Includes:
 * - ErrorCode: emitted by the API (from shared/error-codes registry)
 * - 'TIMEOUT': internal client-side code for fetch timeouts
 */
export type ApiErrorCode = ErrorCode | "TIMEOUT";

class ApiError extends Error {
  code?: ApiErrorCode;
  status: number;
  fields?: Record<string, FieldError | FieldError[]>;

  constructor(
    message: string,
    status: number,
    code?: ApiErrorCode,
    fields?: Record<string, FieldError | FieldError[]>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

// Track if we're currently refreshing to avoid multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;
const inFlightGetRequests = new Map<string, Promise<unknown>>();

/** Clear auth tokens and notify listeners (e.g. layout).
 *  Keys must stay in sync with auth.ts TOKEN_KEY / REFRESH_TOKEN_KEY / USER_KEY. */
function clearAuth() {
  localStorage.removeItem("yuklar_access_token");
  localStorage.removeItem("yuklar_refresh_token");
  localStorage.removeItem("yuklar_user");
  window.dispatchEvent(new Event("auth:signout"));
}

const buildGetDedupeKey = (endpoint: string, options?: RequestInit): string => {
  const headers = options?.headers
    ? JSON.stringify(options.headers)
    : "no-headers";
  const cache = options?.cache ?? "default";
  const credentials = options?.credentials ?? "same-origin";

  return `${endpoint}::${cache}::${credentials}::${headers}`;
};

const REQUEST_TIMEOUT_MS = 30_000;

const request = async <T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false,
): Promise<T> => {
  const { apiBaseUrl } = await loadRuntimeConfig();
  const url = `${apiBaseUrl}${endpoint}`;

  const headers: HeadersInit = {
    ...options.headers,
  };

  const hasJsonBody =
    options.body !== undefined &&
    options.body !== null &&
    !(options.body instanceof FormData);

  if (hasJsonBody) {
    (headers as Record<string, string>)["Content-Type"] =
      (headers as Record<string, string>)["Content-Type"] ?? "application/json";
  }

  // Add auth token and language preference if available
  if (typeof window !== "undefined") {
    const token = localStorage.getItem("yuklar_access_token");
    if (token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }
    const lang = getLanguage();
    if (lang) {
      (headers as Record<string, string>)["Accept-Language"] = lang;
    }
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      signal: options.signal ?? controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError("Request timed out", 0, "TIMEOUT");
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let errorMessage = "An error occurred";
    let errorCode: string | undefined;

    let errorFields: Record<string, FieldError | FieldError[]> | undefined;

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      errorCode = errorData.code;
      if (errorData.fields) errorFields = errorData.fields;
    } catch {
      // Response is not JSON
    }

    // Handle 401 Unauthorized - try to refresh token
    if (
      response.status === 401 &&
      !isRetry &&
      endpoint !== "/access-tokens/refresh-token"
    ) {
      if (typeof window !== "undefined") {
        const refreshToken = localStorage.getItem("yuklar_refresh_token");

        if (refreshToken) {
          try {
            // If already refreshing, wait for that to complete
            if (isRefreshing && refreshPromise) {
              await refreshPromise;
              // Retry the original request with new token
              return request<T>(endpoint, options, true);
            }

            // Start refresh process
            isRefreshing = true;
            refreshPromise = (async () => {
              try {
                // Import refreshAccessToken dynamically to avoid circular dependency
                const { refreshAccessToken } = await import("./auth");
                await refreshAccessToken();
              } finally {
                isRefreshing = false;
                refreshPromise = null;
              }
            })();

            await refreshPromise;

            // Retry the original request with new token
            return request<T>(endpoint, options, true);
          } catch (refreshError) {
            // Refresh failed — clear tokens and let error propagate
            // (no hard redirect so page-level catch blocks can handle it)
            console.error("Token refresh failed:", refreshError);
            clearAuth();
            throw new ApiError("Session expired. Please sign in again.", 401);
          }
        } else {
          // No refresh token — clear tokens and let error propagate
          clearAuth();
        }
      }
    }

    throw new ApiError(
      errorMessage,
      response.status,
      errorCode as ApiErrorCode | undefined,
      errorFields,
    );
  }

  const json = await response.json();
  if ("total" in json && "limit" in json) {
    const { status: _, ...rest } = json;
    return rest; // { data, total, limit, offset }
  }
  return json.data;
};

export const apiClient = {
  get: <T>(endpoint: string, options?: RequestInit) => {
    const dedupeKey = buildGetDedupeKey(endpoint, options);
    const existing = inFlightGetRequests.get(dedupeKey) as
      | Promise<T>
      | undefined;

    if (existing) {
      return existing;
    }

    const nextRequest = request<T>(endpoint, {
      ...options,
      method: "GET",
    }).finally(() => {
      inFlightGetRequests.delete(dedupeKey);
    });

    inFlightGetRequests.set(dedupeKey, nextRequest as Promise<unknown>);
    return nextRequest;
  },

  post: <T>(endpoint: string, data?: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data?: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(endpoint: string, data?: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestInit) =>
    request<T>(endpoint, { ...options, method: "DELETE" }),
};

export { ApiError };
export type { ErrorCode } from "@shared/error-codes.js";
